import { z } from "zod";

export const TASK_STATUSES = ["todo", "in_progress", "done"] as const;
export const TASK_PRIORITIES = ["low", "medium", "high"] as const;

export const taskStatusSchema = z.enum(TASK_STATUSES);
export const taskPrioritySchema = z.enum(TASK_PRIORITIES);

export type TaskStatus = z.infer<typeof taskStatusSchema>;
export type TaskPriority = z.infer<typeof taskPrioritySchema>;

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const createTaskSchema = z.object({
  title: z.string().trim().min(1).max(300),
  description: z.string().max(10_000).nullish(),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  dueAt: z.string().datetime({ offset: true }).nullish(),
  teamId: z.string().uuid().nullish(),
  noteIds: z.array(z.string().uuid()).max(50).optional(),
});

export const updateTaskSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  description: z.string().max(10_000).nullish(),
  status: taskStatusSchema.optional(),
  priority: taskPrioritySchema.optional(),
  dueAt: z.string().datetime({ offset: true }).nullish(),
  /** Set together with `status` when a card is dragged to a new slot. */
  position: z.number().finite().optional(),
});

export const moveTaskSchema = z.object({
  status: taskStatusSchema,
  /** Ids of the cards this one is dropped between, in the target column. */
  afterId: z.string().uuid().nullish(),
  beforeId: z.string().uuid().nullish(),
});

export const linkNotesSchema = z.object({
  noteIds: z.array(z.string().uuid()).max(50),
});

/**
 * Sort key for a card dropped between two neighbours.
 *
 * Midpoints mean a drag rewrites one row rather than renumbering the column.
 * Repeated splits eventually exhaust float precision, so the caller compacts a
 * column when two neighbours land closer than this epsilon.
 */
export const POSITION_EPSILON = 1e-6;
export const POSITION_STEP = 1024;

export function positionBetween(before: number | null, after: number | null): number {
  if (before === null && after === null) return POSITION_STEP;
  if (before === null) return after! - POSITION_STEP;
  if (after === null) return before + POSITION_STEP;
  return (before + after) / 2;
}

/** True when the gap has collapsed and the column needs renumbering. */
export function needsCompaction(before: number | null, after: number | null): boolean {
  return before !== null && after !== null && Math.abs(after - before) < POSITION_EPSILON;
}
